import { CircularProgress, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { type Column, StandardTable } from '../../common/UI/StandardTable';
import { formatBillingName, getRowColor } from './utils';

export const CostBreakdownByFunction: React.FC<{
  loadingData: boolean;
  costsByFunction: Record<string, number>;
}> = ({ loadingData, costsByFunction }) => {
  const theme = useTheme();

  const columns: Column<{ name: string; cost: number }>[] = [
    {
      id: 'name',
      label: <strong>Function / Action</strong>,
      render: (row) => (
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          {row.name}
        </Typography>
      ),
    },
    {
      id: 'cost',
      label: <strong>Estimated Cost</strong>,
      align: 'right',
      render: (row) => (
        <Typography variant="body1">
          $
          {row.cost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
  ];

  const data = Object.entries(
    Object.entries(costsByFunction).reduce(
      (acc, [funcName, cost]) => {
        const formattedName = formatBillingName(funcName);
        acc[formattedName] = (acc[formattedName] || 0) + cost;
        return acc;
      },
      {} as Record<string, number>
    )
  )
    .sort(([, costA], [, costB]) => costB - costA)
    .map(([name, cost]) => ({ name, cost }));

  return (
    <Paper sx={{ p: 3, height: '100%', maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Cost Breakdown (By Function)
      </Typography>
      {loadingData ? (
        <CircularProgress size={24} />
      ) : data.length === 0 ? (
        <Typography color="text.secondary">No recent usage to display.</Typography>
      ) : (
        <StandardTable
          columns={columns}
          data={data}
          keyExtractor={(row) => row.name}
          rowSx={(row) => ({ backgroundColor: getRowColor(theme, row.name) })}
        />
      )}
    </Paper>
  );
};

export const UsageByTender: React.FC<{
  loadingData: boolean;
  costsByTender: Record<string, number>;
}> = ({ loadingData, costsByTender }) => {
  const columns: Column<{ tenderId: string; displayTenderId: string; cost: number }>[] = [
    {
      id: 'tender',
      label: <strong>Tender</strong>,
      render: (row) => row.displayTenderId,
    },
    {
      id: 'cost',
      label: <strong>Estimated Cost</strong>,
      align: 'right',
      render: (row) =>
        `$${row.cost.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const data = Object.entries(costsByTender)
    .sort(([, costA], [, costB]) => costB - costA)
    .map(([tenderId, cost]) => {
      const displayTenderId = tenderId?.includes('_')
        ? tenderId.substring(tenderId.indexOf('_') + 1)
        : tenderId;
      return { tenderId, displayTenderId, cost };
    });

  return (
    <Paper sx={{ p: 3, height: '100%', maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Usage by Tender
      </Typography>
      {loadingData ? (
        <CircularProgress size={24} />
      ) : data.length === 0 ? (
        <Typography color="text.secondary">No recent usage to display.</Typography>
      ) : (
        <StandardTable columns={columns} data={data} keyExtractor={(row) => row.tenderId} />
      )}
    </Paper>
  );
};

export const UsageByTeam: React.FC<{
  loadingData: boolean;
  costsByTeam: Record<string, number>;
  teamsMap: Record<string, string>;
}> = ({ loadingData, costsByTeam, teamsMap }) => {
  const columns: Column<{ teamId: string; name: string; cost: number }>[] = [
    {
      id: 'team',
      label: <strong>Team</strong>,
      render: (row) => row.name,
    },
    {
      id: 'cost',
      label: <strong>Estimated Cost</strong>,
      align: 'right',
      render: (row) =>
        `$${row.cost.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const data = Object.entries(costsByTeam)
    .sort(([, costA], [, costB]) => costB - costA)
    .map(([teamId, cost]) => ({ teamId, name: teamsMap[teamId] || teamId, cost }));

  return (
    <Paper sx={{ p: 3, height: '100%', maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Usage by Team
      </Typography>
      {loadingData ? (
        <CircularProgress size={24} />
      ) : data.length === 0 ? (
        <Typography color="text.secondary">No recent usage to display.</Typography>
      ) : (
        <StandardTable columns={columns} data={data} keyExtractor={(row) => row.teamId} />
      )}
    </Paper>
  );
};

export const UsageByUser: React.FC<{
  loadingData: boolean;
  costsByUser: Record<string, number>;
  usersMap: Record<string, string>;
}> = ({ loadingData, costsByUser, usersMap }) => {
  const columns: Column<{ userId: string; name: string; cost: number }>[] = [
    {
      id: 'user',
      label: <strong>User</strong>,
      render: (row) => row.name,
    },
    {
      id: 'cost',
      label: <strong>Estimated Cost</strong>,
      align: 'right',
      render: (row) =>
        `$${row.cost.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const data = Object.entries(costsByUser)
    .sort(([, costA], [, costB]) => costB - costA)
    .map(([userId, cost]) => ({ userId, name: usersMap[userId] || userId, cost }));

  return (
    <Paper sx={{ p: 3, height: '100%', maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Usage by User
      </Typography>
      {loadingData ? (
        <CircularProgress size={24} />
      ) : data.length === 0 ? (
        <Typography color="text.secondary">No recent usage to display.</Typography>
      ) : (
        <StandardTable columns={columns} data={data} keyExtractor={(row) => row.userId} />
      )}
    </Paper>
  );
};
